import type { FC, ReactNode, SyntheticEvent } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { generatePath } from 'react-router';

import { OrgUnitSelectionStatus } from 'src/constants/orgUnit';
import type { ModuleOrPlanActionOrgUnit } from 'src/types/orgUnit';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpService from 'src/services/api/http';
import { useSection } from 'src/components/app/context/SectionContext';
import { useUserContext } from 'src/components/app/context/UserContext';
import useUserPermissions from 'src/components/app/context/UserContext/useUserPermissions';
import OrgTreeItem from 'src/components/common/OrgTree/components/OrgTreeItem';
import {
  addChildrenToNode,
  orgUnitToMapEntry,
  orgUnitToNode,
} from 'src/components/common/OrgTree/helper';
import type {
  CHECKBOX_MODE,
  OrgTreeState,
  OrgUnitMap,
} from 'src/components/common/OrgTree/types';

export interface OrgTreeValues {
  orgUnitMap: OrgUnitMap;
  fetchChildren: (orgUnitId: number) => void;
  addSearchedOrgUnit: (
    setSelected: (id: string, isSelected: boolean) => void,
    rootElement: HTMLElement | null,
  ) => (orgUnitId: number) => Promise<void>;
  expanded: string[];
  expandItems: (event: SyntheticEvent, itemIds: string[]) => void;
  surveyId?: number;
  moduleId?: number;
  disableOrgUnitSelectionBasedOnStatus?: boolean;
}

export const OrgTreeContext = createContext<OrgTreeValues>({
  orgUnitMap: {},
  fetchChildren: () => {},
  addSearchedOrgUnit: () => () => Promise.resolve(),
  expanded: [],
  expandItems: () => {},
});

interface OrgTreeContextProviderProps
  extends Pick<OrgTreeState, 'setNodes' | 'setOrgUnitMap'> {
  checkboxMode: CHECKBOX_MODE;
  itemsAreDraggable: boolean;
  children: ReactNode;
  initialOrgUnits?: ModuleOrPlanActionOrgUnit[];
  orgUnitsFromPermission?: boolean;
  moduleId?: number;
  customFetchChildrenEndpoint?: (orgUnitId: number) => string;
  surveyIdOverride?: number;
  showPriorityLabels?: boolean;
  disableOrgUnitSelectionBasedOnStatus?: boolean;
}

export const OrgTreeContextProvider: FC<OrgTreeContextProviderProps> = ({
  checkboxMode,
  customFetchChildrenEndpoint,
  itemsAreDraggable,
  children,
  initialOrgUnits,
  orgUnitsFromPermission = false,
  moduleId,
  setNodes,
  setOrgUnitMap,
  surveyIdOverride,
  showPriorityLabels,
  disableOrgUnitSelectionBasedOnStatus,
}) => {
  const [expanded, setExpanded] = useState<string[]>([]);
  const expandItems = (_: SyntheticEvent, itemIds: string[]) => {
    setExpanded(itemIds);
  };

  const { activeUserPermissionsInCurrentSurvey } = useUserPermissions();
  const { orgUnits } = useSection();
  const { currentSurvey } = useUserContext();

  const orgUnitMap = useRef<OrgUnitMap>({});

  const surveyId = useMemo(
    () => surveyIdOverride ?? currentSurvey?.id,
    [surveyIdOverride, currentSurvey],
  );

  const defaultOrgUnits = useMemo(() => {
    if (initialOrgUnits) {
      return initialOrgUnits;
    }
    if (orgUnitsFromPermission) {
      return (
        activeUserPermissionsInCurrentSurvey.find(
          (permission) => permission.module.id === moduleId,
        )?.org_units || []
      );
    }
    return orgUnits || [];
  }, [
    initialOrgUnits,
    activeUserPermissionsInCurrentSurvey,
    orgUnits,
    moduleId,
    orgUnitsFromPermission,
  ]);

  const createLabel = useCallback(
    (orgUnit: ModuleOrPlanActionOrgUnit) => (
      <OrgTreeItem
        draggable={itemsAreDraggable}
        orgUnit={orgUnit}
        showPriorityLabel={showPriorityLabels}
      />
    ),
    [itemsAreDraggable, showPriorityLabels],
  );

  useEffect(() => {
    setNodes(
      defaultOrgUnits.map((defaultOrgUnit) => ({
        ...orgUnitToNode(
          !!checkboxMode,
          fetchChildren,
          createLabel,
        )(defaultOrgUnit),
        showPriority: true,
      })),
    );

    orgUnitMap.current = Object.assign(
      {},
      ...defaultOrgUnits.map(orgUnitToMapEntry),
    );
    setOrgUnitMap(orgUnitMap.current);
  }, [defaultOrgUnits]);

  const getFetchChildrenEndpoint = useCallback(
    (orgUnitId: number) => {
      if (customFetchChildrenEndpoint) {
        return customFetchChildrenEndpoint(orgUnitId);
      }

      return generatePath(apiPaths.ORG_UNIT_CHILDREN, {
        id: orgUnitId,
      });
    },
    [customFetchChildrenEndpoint],
  );

  const fetchChildren = (orgUnitId: number) => {
    const parent = orgUnitMap.current[orgUnitId];

    if (parent && !parent.orgUnit.is_leaf && !parent.childrenRequested) {
      const params = { module_id: moduleId };
      const endpoint = getFetchChildrenEndpoint(orgUnitId);

      parent.childrenRequested = true;

      return HttpService.get(endpoint, params, false)
        .then(({ data }) => insertChildren(orgUnitId, data))
        .catch((error) => {
          if (error?.response?.status === 404) {
            // 404 means no children exist
            insertChildren(orgUnitId, []);
            return;
          }
          parent.childrenRequested = false;
        });
    }

    return Promise.resolve();
  };

  const insertChildren = (
    parentId: number,
    children: ModuleOrPlanActionOrgUnit[],
  ) => {
    const parent = orgUnitMap.current[parentId];

    if (parent && !parent.orgUnit.is_leaf && !parent.childrenLoaded) {
      parent.childrenLoaded = true;
      parent.childrenRequested = true;

      Object.assign(
        orgUnitMap.current,
        ...children
          .filter(({ id }) => !(id in orgUnitMap))
          .map(orgUnitToMapEntry),
      );

      const converter = orgUnitToNode(
        !!checkboxMode,
        fetchChildren,
        createLabel,
      );

      setNodes((prev) =>
        addChildrenToNode(children, parentId, converter, prev),
      );
    }
  };

  const addSearchedOrgUnit = useCallback(
    (
      setSelected: (id: string, isSelected: boolean) => void,
      rootElement: HTMLElement | null,
    ) =>
      (orgUnitId: number) => {
        const params = { module_id: moduleId };
        const endpoint = generatePath(apiPaths.ORG_UNIT_ANCESTORS_BY_SURVEY, {
          orgUnitId: orgUnitId.toString(),
          surveyId,
        });

        return HttpService.get(endpoint, params, false)
          .then(({ data }) => {
            const orgUnit = data[data.length - 1][0];

            data.forEach(
              (
                tuple: [ModuleOrPlanActionOrgUnit, ModuleOrPlanActionOrgUnit[]],
              ) => {
                const [parent, children] = tuple;
                insertChildren(parent.id, children);
              },
            );
            setSelected(String(orgUnit.id), true);
            setExpanded((prev: string[]) => [
              ...prev,
              ...data.map(
                (
                  tuple: [
                    ModuleOrPlanActionOrgUnit,
                    ModuleOrPlanActionOrgUnit[],
                  ],
                ) => tuple[0].id.toString(),
              ),
            ]);

            return orgUnit;
          })
          .then((orgUnit) => {
            if (
              rootElement &&
              (!disableOrgUnitSelectionBasedOnStatus ||
                (disableOrgUnitSelectionBasedOnStatus &&
                  orgUnit.selection_status !==
                    OrgUnitSelectionStatus.LESS_THAN_MIN_RESPONDENT_AMOUNT &&
                  orgUnit.selection_status !==
                    OrgUnitSelectionStatus.NEGATIVE_SELECTION))
            ) {
              // wait for animations to finish and scroll selected element into view afterwards
              setTimeout(() => {
                const item = rootElement.querySelector(
                  `.org-tree-item-${orgUnit.id}`,
                );
                if (item) {
                  item.scrollIntoView({
                    behavior: 'smooth',
                  });
                }
              }, 500);
            }
          });
      },
    [moduleId, insertChildren, surveyId],
  );

  return (
    <OrgTreeContext.Provider
      value={{
        orgUnitMap: orgUnitMap.current,
        fetchChildren,
        addSearchedOrgUnit,
        expanded,
        expandItems,
        surveyId,
        moduleId,
        disableOrgUnitSelectionBasedOnStatus,
      }}
    >
      {children}
    </OrgTreeContext.Provider>
  );
};

const useOrgTree = () => useContext(OrgTreeContext);
export default useOrgTree;
